import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { apiErr, fmtNum } from "@/lib/format";
import { toast } from "sonner";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";
import { PlusIcon, PencilSimpleIcon, MapTrifoldIcon, TrashIcon } from "@phosphor-icons/react";

/**
 * REGIONES DE TRABAJO — catálogo único para Clientes, Empleados, Ventas, RTC, Reportes y Rutas.
 * Crear, renombrar (se propaga a los módulos vinculados), activar / desactivar y eliminar las que nunca se usaron.
 */
const empty = { codigo: "", nombre: "", descripcion: "", activo: true };

export default function Regions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("todas");     // todas | activas | inactivas
  const [form, setForm] = useState(null);           // null = cerrado
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!items.length) setLoading(true);           // al recargar no se vacía la tabla
    try { setItems((await api.get("/regions", { params: { uso: true } })).data || []); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const guardar = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form, codigo: form.codigo.trim().toUpperCase(), nombre: (form.nombre || form.codigo).trim() };
      if (form.id) { await api.put(`/regions/${form.id}`, body); toast.success("Región actualizada (se propagó a clientes, empleados y ventas)"); }
      else { await api.post("/regions", body); toast.success("Región creada"); }
      setForm(null); load();
    } catch (er) { toast.error(apiErr(er)); }
    finally { setSaving(false); }
  };

  const m = useModalForm({ value: form || undefined, abierto: Boolean(form), onClose: () => setForm(null), onSave: guardar });

  const toggle = async (r) => {
    const n = r.uso?.clientes_activos || 0;
    if (r.activo && !window.confirm(
      `¿Desactivar la región ${r.codigo}?` + (n ? `\n\nTiene ${n} cliente(s) activo(s): conservan su región, pero ya no podrá elegirse para registros nuevos.` : ""))) return;
    try {
      if (r.activo) await api.delete(`/regions/${r.id}`);
      else await api.put(`/regions/${r.id}`, { ...r, activo: true });
      toast.success(`Región ${r.codigo} ${r.activo ? "desactivada" : "activada"}`);
      load();
    } catch (er) { toast.error(apiErr(er)); }
  };

  const eliminar = async (r) => {
    if (!window.confirm(`¿Eliminar definitivamente la región ${r.codigo}? Solo es posible porque no tiene registros vinculados.`)) return;
    try { await api.delete(`/regions/${r.id}/definitivo`); toast.success(`Región ${r.codigo} eliminada`); load(); }
    catch (er) { toast.error(apiErr(er)); }
  };

  const visibles = items.filter((r) => filtro === "todas" || (filtro === "activas" ? r.activo : !r.activo));
  const activas = items.filter((r) => r.activo).length;
  const sinUso = (r) => !Object.values(r.uso || {}).some(Boolean);

  return (
    <Layout title="Regiones de trabajo" subtitle={`${activas} activas de ${items.length} · catálogo único para clientes, RTC, ventas y reportes`}
      actions={
        <button onClick={() => setForm(empty)} data-testid="new-region-btn"
          className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2">
          <PlusIcon size={16} weight="bold" /> Nueva región
        </button>
      }>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="inline-flex border border-border rounded-md overflow-hidden text-xs" data-testid="regions-filter">
          {[["todas", "Todas"], ["activas", "Activas"], ["inactivas", "Inactivas"]].map(([v, l]) => (
            <button key={v} onClick={() => setFiltro(v)} className={`px-3 py-1.5 ${filtro === v ? "bg-agro-green/15 text-agro-green" : "text-text-secondary hover:text-text-primary"}`}>{l}</button>
          ))}
        </div>
        <div className="text-[11px] text-text-muted max-w-xl">
          Renombrar actualiza clientes, empleados y ventas. Desactivar conserva el historial y evita que se elija en registros
          nuevos. Solo se elimina una región que nunca se usó.
        </div>
      </div>

      <div className="bg-card border border-border/60 rounded-lg overflow-hidden" data-testid="regions-table">
        <table className="w-full text-sm">
          <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
            <tr className="text-left">
              <th className="p-3 pl-5">Región</th><th className="p-3">Descripción</th>
              <th className="p-3 text-right">Clientes</th><th className="p-3 text-right">RTC asignados</th>
              <th className="p-3 text-right">Empleados</th><th className="p-3">Estado</th><th className="p-3 pr-5 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="p-8 text-center text-text-muted">Cargando…</td></tr>
              : visibles.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-text-muted">Sin regiones</td></tr>
              : visibles.map((r) => (
                <tr key={r.id} className={`border-t border-border/40 ${r.activo ? "" : "opacity-60"}`} data-testid={`region-row-${r.codigo}`}>
                  <td className="p-3 pl-5">
                    <div className="flex items-center gap-2"><MapTrifoldIcon size={16} className="text-agro-green" />
                      <span className="font-mono font-semibold">{r.codigo}</span>
                      {r.nombre && r.nombre !== r.codigo && <span className="text-text-secondary">· {r.nombre}</span>}</div>
                  </td>
                  <td className="p-3 text-xs text-text-secondary">{r.descripcion || "—"}</td>
                  <td className="p-3 text-right">{fmtNum(r.uso?.clientes_activos || 0)}{r.uso?.clientes > (r.uso?.clientes_activos || 0) && <span className="text-[10px] text-text-muted"> / {r.uso.clientes}</span>}</td>
                  <td className="p-3 text-right">{fmtNum(r.uso?.usuarios_rtc || 0)}</td>
                  <td className="p-3 text-right">{fmtNum(r.uso?.empleados || 0)}</td>
                  <td className="p-3">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${r.activo ? "border-agro-green/40 text-agro-green" : "border-border text-text-muted"}`}>
                      {r.activo ? "Activa" : "Inactiva"}</span>
                  </td>
                  <td className="p-3 pr-5">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setForm({ ...r })} className="text-xs px-2.5 py-1.5 border border-border rounded-md text-text-secondary hover:border-agro-green/40 flex items-center gap-1" data-testid={`edit-region-${r.codigo}`}>
                        <PencilSimpleIcon size={12} /> Editar</button>
                      <button onClick={() => toggle(r)} data-testid={`toggle-region-${r.codigo}`}
                        className={`text-xs px-2.5 py-1.5 border rounded-md ${r.activo ? "border-red-500/30 text-red-400 hover:bg-red-500/10" : "border-agro-green/30 text-agro-green hover:bg-agro-green/10"}`}>
                        {r.activo ? "Desactivar" : "Activar"}</button>
                      {sinUso(r) && (
                        <button onClick={() => eliminar(r)} title="Eliminar (nunca se usó)" data-testid={`delete-region-${r.codigo}`}
                          className="text-xs px-2 py-1.5 border border-border rounded-md text-text-muted hover:text-red-400 hover:border-red-400/40"><TrashIcon size={12} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
          <form onSubmit={m.guardar} className="bg-card border border-border rounded-lg w-full max-w-md" data-testid="region-modal">
            <div className="p-5 border-b border-border flex justify-between">
              <h3 className="font-heading text-lg font-semibold">{form.id ? `Editar región ${form.codigo}` : "Nueva región"}</h3>
              <BotonCerrar onClick={m.cerrar} />
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Código *</label>
                <input required value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })} placeholder="R6"
                  data-testid="form-region-codigo" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm font-mono uppercase" />
                {form.id && <div className="text-[10px] text-agro-yellow mt-1">Cambiar el código lo actualiza en clientes, empleados, RTC y ventas.</div>}
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Nombre</label>
                <input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Zona Sur"
                  data-testid="form-region-nombre" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Descripción / cobertura</label>
                <input value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} placeholder="Municipios o zona que cubre"
                  className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" />
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.activo !== false}
                onChange={(e) => setForm({ ...form, activo: e.target.checked })} /> Activa</label>
            </div>
            <div className="p-5 border-t border-border flex justify-end gap-3">
              <button type="button" onClick={m.cerrar} className="px-4 py-2 text-sm border border-border rounded-md">Cancelar</button>
              <button disabled={saving || m.saving || !form.codigo.trim()} data-testid="form-region-submit" className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md disabled:opacity-50">
                {form.id ? "Guardar cambios" : "Crear región"}</button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
